import React, { useCallback, useEffect, useState } from 'react';
import { Calendar, Play, AlertCircle } from 'lucide-react';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { useTimecardReportFilters } from '../hooks/useTimecardReportFilters';
import TimecardReportFiltersPanel from '../components/TimecardReportFiltersPanel';
import type { WeeklyTotalsFilterParams } from '../utils/weeklyTotalsReport';
import { formatCompanyDate } from '../utils/companyDateFormat';

interface TimecardsWeeklyTotalsProps {
  onGenerateReport: (request: {
    reportType: 'timecards-weekly-totals';
    parameters: WeeklyTotalsFilterParams;
  }) => void;
}

const TimecardsWeeklyTotals: React.FC<TimecardsWeeklyTotalsProps> = ({ onGenerateReport }) => {
  const weekStartsOn = useWeekStartDay();
  const [fromWeekStart, setFromWeekStart] = useState<string>('');
  const [toWeekStart, setToWeekStart] = useState<string>('');

  const {
    statusFilter,
    setStatusFilter,
    selectedEmployeeIds,
    setSelectedEmployeeIds,
    selectedScheduleIds,
    setSelectedScheduleIds,
    selectedPositionIds,
    setSelectedPositionIds,
    employees,
    schedules,
    positions,
    loadingOptions,
    buildCommonFilters,
  } = useTimecardReportFilters();

  const getCurrentWeekStart = useCallback(() => {
    const today = new Date();
    const day = today.getDay();
    let diff = day - weekStartsOn;
    if (diff < 0) diff += 7;
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - diff);
    return weekStart.toISOString().split('T')[0];
  }, [weekStartsOn]);

  useEffect(() => {
    if (!fromWeekStart && weekStartsOn !== undefined) {
      const currentWeek = getCurrentWeekStart();
      setFromWeekStart(currentWeek);
      const toDate = new Date(currentWeek);
      toDate.setDate(toDate.getDate() + 7 * 7);
      setToWeekStart(toDate.toISOString().split('T')[0]);
    }
  }, [weekStartsOn, fromWeekStart, getCurrentWeekStart]);

  const getWeekStartFromDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = date.getDay();
    let diff = day - weekStartsOn;
    if (diff < 0) diff += 7;
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - diff);
    return weekStart.toISOString().split('T')[0];
  };

  const handleWeekChange = (type: 'from' | 'to', direction: 'prev' | 'next') => {
    const currentDate = type === 'from' ? new Date(fromWeekStart) : new Date(toWeekStart);
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));

    if (type === 'from') {
      setFromWeekStart(newDate.toISOString().split('T')[0]);
    } else {
      setToWeekStart(newDate.toISOString().split('T')[0]);
    }
  };

  const handleDateChange = (type: 'from' | 'to', dateString: string) => {
    const weekStart = getWeekStartFromDate(dateString);
    if (type === 'from') {
      setFromWeekStart(weekStart);
    } else {
      setToWeekStart(weekStart);
    }
  };

  const formatWeekRange = (startDate: string) => {
    const start = new Date(startDate);
    const end = new Date(startDate);
    end.setDate(start.getDate() + 6);
    return `${formatCompanyDate(start)} - ${formatCompanyDate(end)}`;
  };

  const calculateWeekCount = () => {
    if (!fromWeekStart || !toWeekStart) return 0;
    const from = new Date(fromWeekStart);
    const to = new Date(toWeekStart);
    const diffTime = to.getTime() - from.getTime();
    const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7)) + 1;
    return Math.max(0, Math.min(diffWeeks, 13));
  };

  const weekCount = calculateWeekCount();
  const isValidRange =
    fromWeekStart && toWeekStart && new Date(fromWeekStart) <= new Date(toWeekStart) && weekCount <= 13;

  const handleGenerateReport = () => {
    if (!isValidRange) return;

    onGenerateReport({
      reportType: 'timecards-weekly-totals',
      parameters: {
        fromWeekStartDate: fromWeekStart,
        toWeekStartDate: toWeekStart,
        ...buildCommonFilters(),
      },
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900">Weekly Totals Report</h2>
        <p className="text-sm text-neutral-600 mt-1">
          View weekly hour totals for employees across multiple weeks (up to 13 weeks)
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-lg border border-neutral-200 p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-3">
                <Calendar className="inline h-4 w-4 mr-2" />
                From Week
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleWeekChange('from', 'prev')}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  ←
                </button>
                <div className="flex-1 min-w-0">
                  <input
                    type="date"
                    value={fromWeekStart}
                    onChange={(e) => handleDateChange('from', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm"
                  />
                  <div className="text-xs text-neutral-600 mt-1">
                    {fromWeekStart && formatWeekRange(fromWeekStart)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleWeekChange('from', 'next')}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  →
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-3">
                <Calendar className="inline h-4 w-4 mr-2" />
                To Week
              </label>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleWeekChange('to', 'prev')}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  ←
                </button>
                <div className="flex-1 min-w-0">
                  <input
                    type="date"
                    value={toWeekStart}
                    onChange={(e) => handleDateChange('to', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md text-sm"
                  />
                  <div className="text-xs text-neutral-600 mt-1">
                    {toWeekStart && formatWeekRange(toWeekStart)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handleWeekChange('to', 'next')}
                  className="px-3 py-2 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-md hover:bg-neutral-50"
                >
                  →
                </button>
              </div>
            </div>
          </div>

          <TimecardReportFiltersPanel
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            employees={employees}
            schedules={schedules}
            positions={positions}
            selectedEmployeeIds={selectedEmployeeIds}
            onEmployeeIdsChange={setSelectedEmployeeIds}
            selectedScheduleIds={selectedScheduleIds}
            onScheduleIdsChange={setSelectedScheduleIds}
            selectedPositionIds={selectedPositionIds}
            onPositionIdsChange={setSelectedPositionIds}
            loadingOptions={loadingOptions}
          />

          <div className="pt-2">
            <button
              type="button"
              onClick={handleGenerateReport}
              disabled={!isValidRange}
              className="w-full max-w-xs flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-neutral-300 disabled:cursor-not-allowed transition-colors"
            >
              <Play className="h-4 w-4" />
              Generate Report
            </button>
          </div>

          {isValidRange && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-900">
                <span className="font-medium">Selected Range:</span> {weekCount} week{weekCount !== 1 ? 's' : ''}
                {weekCount < 13 && ` · ${13 - weekCount} additional columns will show zeros`}
              </p>
            </div>
          )}

          {!isValidRange && fromWeekStart && toWeekStart && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-900">
                  {new Date(fromWeekStart) > new Date(toWeekStart)
                    ? 'From Week must be before or equal to To Week'
                    : 'Maximum range is 13 weeks'}
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-4 h-fit">
          <h3 className="text-sm font-semibold text-neutral-900 mb-3">Report Structure</h3>
          <div className="text-sm text-neutral-700 space-y-2">
            <p>• Week 1 through Week 13, plus Total</p>
            <p>• Weeks in range show date in brackets</p>
            <p>• Data shown for all weeks if exists</p>
            <p className="text-xs text-neutral-600 mt-3 pt-3 border-t border-neutral-300">
              <span className="font-medium">Note:</span> Use filters to narrow by status, schedule, position, or
              employee. Leave filters empty to include all.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimecardsWeeklyTotals;
